import { CheckCircleIcon, XCircleIcon } from '@phosphor-icons/react';

import { ActionMenu } from '@/components/common/action-menu';
import type { Role } from '@/types';

import { canReviewRequest } from '../permissions';
import type { ReplacementRow } from '../replacements/types';

/** Row ellipsis for the replacements queue (Figma 30:407098). Actions exist **only while
 *  Pending** (ADR-0019, grilling Q5) — approved/rejected are final, so the menu is absent.
 *  Approve precedes Reject (Figma 30:407098); hover-only reveal is CSS on the queue page. */
export function ReplacementActionsMenu({
  row,
  viewerRole,
  onApprove,
  onReject,
}: {
  row: ReplacementRow;
  /** The signed-in admin's role, from `useAuth().user` on the queue page. Required, not
   *  optional: a default would re-open this gate the next time a queue is built. */
  viewerRole: Role | null;
  onApprove: (row: ReplacementRow) => void;
  onReject: (row: ReplacementRow) => void;
}) {
  if (row.status !== 'pending') return null;
  // Row status decides whether the request is actionable at all; the viewer's role decides
  // whether *this* admin may act on it. Both must hold. No ellipsis rather than disabled items:
  // every other branch in this file returns `null`, and an Operational Associate's view of the
  // queue is read-only, so an always-dead trigger would be chrome with nothing behind it.
  if (!canReviewRequest(viewerRole)) return null;

  return (
    <ActionMenu
      triggerLabel={`Actions for ${row.orderNumber}`}
      groups={[
        {
          items: [
            {
              key: 'approve',
              label: 'Approve',
              icon: <CheckCircleIcon size={16} />,
              onClick: () => onApprove(row),
            },
            {
              key: 'reject',
              label: 'Reject',
              icon: <XCircleIcon size={16} />,
              danger: true,
              onClick: () => onReject(row),
            },
          ],
        },
      ]}
    />
  );
}
